import React from 'react';
import { Calendar, CheckCircle, AlertCircle, TrendingUp, Target, Building, Users, Sparkles } from 'lucide-react';
import TaskList from '../TaskList';
import JourneyProgress from '../journey/JourneyProgress'; 
import DepartmentUpdates from './DepartmentUpdates';
import { useEntity } from '../../hooks/useEntity';

export default function BusinessOverview() {
  const { entity } = useEntity();
  const [hasTasks, setHasTasks] = React.useState<boolean>(false);
  
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // A simplified check that doesn't cause reload loops
  React.useEffect(() => {
    // Just set to false by default - we'll show example tasks instead
    setHasTasks(false);
  }, [entity?.id]);

  return (
    <div className="space-y-6">
      {/* Welcome Message for New Users */}
      {!hasTasks && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-8 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">Welcome to Your Command Center</h1>
              <p className="text-blue-100 mb-4">
                This is your business headquarters where you'll track progress, manage tasks, and monitor your business health.
              </p>
              <p className="text-blue-100">
                To get started:
              </p>
              <ul className="list-disc list-inside text-blue-100 mt-2 space-y-1">
                <li>Visit the Workshop to create your first project</li>
                <li>Set up your business vision in the Development floor</li>
                <li>Configure your company profile in Operations</li>
              </ul>
            </div>
            <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
              <Sparkles className="w-8 h-8" />
            </div>
          </div>
        </div>
      )}
      
      {/* Status Cards - Only show if user has tasks */}
      {hasTasks && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-green-900">Business Health</h3>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-700 mt-2">Good</p>
            <p className="text-sm text-green-600 mt-1">All systems operational</p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-yellow-900">Team Activity</h3>
              <Users className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-yellow-700 mt-2">Active</p>
            <p className="text-sm text-yellow-600 mt-1">2 departments engaged</p>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-blue-900">Goals Progress</h3>
              <Target className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-700 mt-2">50%</p>
            <p className="text-sm text-blue-600 mt-1">Q1 objectives on track</p>
          </div>
        </div>
      )}

      {/* Tasks Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">{hasTasks ? "Needs Immediate Attention" : "Example Tasks"}</h2>
            <span className="text-sm text-gray-500">({hasTasks ? "From Workshop" : "Get Started with These"})</span>
          </div>
          <AlertCircle className="w-6 h-6 text-blue-500" />
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-100">
          {hasTasks ? (
            <TaskList limit={4} showAddButton={false} />
          ) : (
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-gray-300" />
                </div>
                <div className="flex-grow">
                  <p className="text-gray-900">Create your first project in Workshop</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-yellow-500">Medium Priority</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center ml-2">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Getting Started
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-gray-300" />
                </div>
                <div className="flex-grow">
                  <p className="text-gray-900">Define your business vision</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-red-500">High Priority</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center ml-2">
                      <Target className="w-3 h-3 mr-1" />
                      Business Planning
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-gray-300" />
                </div>
                <div className="flex-grow">
                  <p className="text-gray-900">Set up your company profile</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-yellow-500">Medium Priority</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center ml-2">
                      <Building className="w-3 h-3 mr-1" />
                      Organization
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-gray-300" />
                </div>
                <div className="flex-grow">
                  <p className="text-gray-900">Add your first team contact</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-500">Low Priority</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center ml-2">
                      <Users className="w-3 h-3 mr-1" />
                      Team Building
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="w-full text-blue-500 hover:text-blue-600 text-sm py-2 text-center">
                + Visit Workshop to create your first real task
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Journey Progress */}
      <JourneyProgress />

      {/* Department Updates - Only show if user has tasks */}
      {hasTasks && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Department Updates</h2>
            <TrendingUp className="w-6 h-6 text-blue-500" />
          </div>
          <DepartmentUpdates />
        </div>
      )}
    </div>
  );
}